/**
 * Text rendering for the platform previews.
 *
 * Two jobs:
 *  1. Turn a plain caption into the entities each platform highlights (links,
 *     hashtags, mentions) with that platform's own link colour.
 *  2. Render the WordPress body. Draft bodies come back as HTML from the model,
 *     so it is walked through a strict allowlist instead of `dangerouslySetInnerHTML` —
 *     unknown tags are unwrapped, attributes are dropped, and only http(s) and
 *     app-relative links survive.
 */

import { createElement } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { PLATFORM_SPECS } from '../../../shared/platforms';
import type { PlatformId } from '../../../shared/types';

/* ------------------------------------------------------------------ */
/* Entity highlighting                                                 */
/* ------------------------------------------------------------------ */

/** Link colours as each platform actually renders them, light and dark. */
const LINK_COLORS: Record<PlatformId, { light: string; dark: string }> = {
  facebook: { light: '#1877F2', dark: '#5AA0FB' },
  instagram: { light: '#00376B', dark: '#A8D3FF' },
  x: { light: '#1D9BF0', dark: '#4FB4F5' },
  linkedin: { light: '#0A66C2', dark: '#5CA8E8' },
  wordpress: { light: '#21759B', dark: '#5FAFD1' },
  tiktok: { light: '#FE2C55', dark: '#FF6B85' },
  youtube: { light: '#065FD4', dark: '#6BA6F5' },
};

export function linkStyle(platform: PlatformId): CSSProperties {
  const colors = LINK_COLORS[platform];
  return {
    '--pap-link': colors.light,
    '--pap-link-dark': colors.dark,
  } as CSSProperties;
}

const LINK_CLASS = 'text-[var(--pap-link)] dark:text-[var(--pap-link-dark)]';

const ENTITY = /(https?:\/\/[^\s<]+|#[\p{L}\p{N}_]+|@[A-Za-z0-9_.]{2,30})/gu;

/** X shows a shortened display URL; everyone else shows the link verbatim. */
function displayUrl(url: string, shorten: boolean): string {
  if (!shorten) return url;
  try {
    const parsed = new URL(url);
    const tail = `${parsed.pathname}${parsed.search}`.replace(/\/$/, '');
    const host = parsed.host.replace(/^www\./, '');
    return tail && tail !== '/' ? `${host}${tail.length > 16 ? `${tail.slice(0, 16)}…` : tail}` : host;
  } catch {
    return url;
  }
}

export interface EntityTextOptions {
  /** Shorten URLs the way X's composer does. */
  shortenUrls?: boolean;
}

/**
 * Split a caption into plain text and highlighted entities. Returns an array of
 * nodes so it can drop straight into a `<p>` with `whitespace-pre-wrap`.
 */
export function entityText(
  text: string,
  platform: PlatformId,
  options: EntityTextOptions = {},
): ReactNode[] {
  if (!text) return [];
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  ENTITY.lastIndex = 0;
  for (const match of text.matchAll(ENTITY)) {
    const index = match.index ?? 0;
    const value = match[0];
    if (index > lastIndex) nodes.push(text.slice(lastIndex, index));
    const label = value.startsWith('http') ? displayUrl(value, Boolean(options.shortenUrls)) : value;
    nodes.push(
      <span key={`entity-${key}`} className={LINK_CLASS}>
        {label}
      </span>,
    );
    key += 1;
    lastIndex = index + value.length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

/**
 * Split a caption at the platform's character limit, counting URLs the way the
 * platform does. The tail is what a preview paints red — exactly what would be
 * rejected on publish.
 */
export function splitAtLimit(platform: PlatformId, text: string): [string, string] {
  const spec = PLATFORM_SPECS[platform];
  if (!spec || spec.limits.unlimitedChars) return [text, ''];

  const max = spec.limits.maxChars;
  const weight = spec.limits.urlWeight;
  const urlStarts = new Map<number, number>();
  if (weight > 0) {
    for (const match of text.matchAll(/https?:\/\/[^\s]+/g)) {
      const start = match.index ?? 0;
      urlStarts.set(start, start + match[0].length);
    }
  }

  let cost = 0;
  let index = 0;
  while (index < text.length) {
    const urlEnd = urlStarts.get(index);
    if (urlEnd !== undefined) {
      cost += weight;
      if (cost > max) return [text.slice(0, index), text.slice(index)];
      index = urlEnd;
      continue;
    }
    const codePoint = text.codePointAt(index);
    const size = codePoint !== undefined && codePoint > 0xffff ? 2 : 1;
    cost += 1;
    if (cost > max) return [text.slice(0, index), text.slice(index)];
    index += size;
  }
  return [text, ''];
}

/**
 * Caption body with the over-limit tail highlighted, as X and LinkedIn do in
 * their own composers.
 */
export function entityTextWithOverflow(
  text: string,
  platform: PlatformId,
  options: EntityTextOptions = {},
): ReactNode {
  const [within, overflow] = splitAtLimit(platform, text);
  if (!overflow) return entityText(within, platform, options);
  return (
    <>
      {entityText(within, platform, options)}
      <span className="rounded-[2px] bg-danger/25 text-fg">{overflow}</span>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Article body (WordPress)                                            */
/* ------------------------------------------------------------------ */

const ALLOWED_TAGS = new Set([
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'a',
  'ul',
  'ol',
  'li',
  'blockquote',
  'h1',
  'h2',
  'h3',
  'h4',
  'code',
  'pre',
  'hr',
  'figure',
  'figcaption',
  'img',
]);

const TAG_CLASS: Record<string, string> = {
  p: 'mb-4 leading-7 text-[15px]',
  h1: 'mb-3 mt-6 text-xl font-semibold tracking-tight',
  h2: 'mb-3 mt-6 text-lg font-semibold tracking-tight',
  h3: 'mb-2 mt-5 text-base font-semibold',
  h4: 'mb-2 mt-4 text-sm font-semibold uppercase tracking-wide text-muted',
  ul: 'mb-4 list-disc space-y-1.5 pl-5 text-[15px] leading-7 marker:text-subtle',
  ol: 'mb-4 list-decimal space-y-1.5 pl-5 text-[15px] leading-7 marker:text-subtle',
  li: 'pl-1',
  blockquote: 'mb-4 border-l-2 border-accent/60 pl-4 italic text-muted',
  pre: 'mb-4 overflow-x-auto rounded-md bg-sunken p-3 font-mono text-xs leading-6',
  code: 'rounded bg-sunken px-1 py-0.5 font-mono text-[13px]',
  hr: 'my-6 border-t border-border',
  figure: 'mb-5',
  figcaption: 'mt-2 text-xs text-subtle',
  img: 'w-full rounded-lg border border-border',
  strong: 'font-semibold text-fg',
  b: 'font-semibold text-fg',
  em: 'italic',
  i: 'italic',
  u: 'underline underline-offset-2',
};

const VOID_TAGS = new Set(['br', 'hr', 'img']);

/** Tags whose *contents* are dropped too, not just their wrapper. */
const DROP_TAGS = new Set([
  'script',
  'style',
  'noscript',
  'template',
  'iframe',
  'object',
  'embed',
  'svg',
  'form',
  'input',
  'button',
]);

function safeHref(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('/') || trimmed.startsWith('#')) {
    return trimmed;
  }
  return null;
}

function walk(node: Node, keyPrefix: string, index: number): ReactNode {
  if (node.nodeType === 3) return node.nodeValue ?? '';
  if (node.nodeType !== 1) return null;

  const element = node as Element;
  const tag = element.tagName.toLowerCase();
  if (DROP_TAGS.has(tag)) return null;

  const key = `${keyPrefix}-${index}`;
  const children = Array.from(element.childNodes)
    .map((child, childIndex) => walk(child, key, childIndex))
    .filter((child) => child !== null && child !== '');

  if (!ALLOWED_TAGS.has(tag)) {
    // Unknown tag: keep the words, drop the wrapper.
    return children.length ? <span key={key}>{children}</span> : null;
  }

  const props: Record<string, unknown> = { key, className: TAG_CLASS[tag] };

  if (tag === 'a') {
    const href = safeHref(element.getAttribute('href'));
    if (!href) return <span key={key}>{children}</span>;
    props.href = href;
    props.target = '_blank';
    props.rel = 'noreferrer noopener';
    props.className = 'text-accent underline underline-offset-2';
  }

  if (tag === 'img') {
    const src = safeHref(element.getAttribute('src'));
    if (!src) return null;
    props.src = src;
    props.alt = element.getAttribute('alt') ?? '';
    props.loading = 'lazy';
  }

  if (VOID_TAGS.has(tag)) return createElement(tag, props);
  return createElement(tag, props, children);
}

/** True when the string carries real markup rather than plain paragraphs. */
export function looksLikeHtml(value: string): boolean {
  return /<\/?(p|div|h[1-6]|ul|ol|li|br|blockquote|figure|img|strong|em|a)\b/i.test(value);
}

/**
 * Render a WordPress draft body. HTML goes through the allowlist above; plain
 * text is split into paragraphs so the article preview still reads like an article.
 */
export function renderArticleBody(body: string): ReactNode {
  const trimmed = body.trim();
  if (!trimmed) return null;

  if (!looksLikeHtml(trimmed) || typeof DOMParser === 'undefined') {
    return trimmed.split(/\n{2,}/).map((paragraph, index) => (
      <p key={`para-${index}`} className={TAG_CLASS.p}>
        {paragraph.split('\n').map((line, lineIndex, lines) => (
          <span key={`line-${lineIndex}`}>
            {line}
            {lineIndex < lines.length - 1 && <br />}
          </span>
        ))}
      </p>
    ));
  }

  try {
    const parsed = new DOMParser().parseFromString(trimmed, 'text/html');
    return Array.from(parsed.body.childNodes).map((node, index) => walk(node, 'wp', index));
  } catch {
    return <p className={TAG_CLASS.p}>{trimmed}</p>;
  }
}
